'use client';

import {
  useState,
  createContext,
  useContext,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from 'react';
import amplitudeService from '@features/analytics/amplitudeService';
import ShareIcon from '@/icons/share.svg';
import LinkIcon from '@/icons/link.svg';
import PlayIcon from '@/icons/play.svg';
import {Button} from '@features/ui/components/Button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@features/ui/components/tooltip';
import {Link} from '@tanstack/react-router';
import {useNavigate} from '@tanstack/react-router';
import {ExportModal} from './ExportModal';
import {WorkspaceSelector} from './WorkspaceSelector';
import {teamWorkspacesEnabled} from '@features/utils/featureFlags';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from '@features/ui/components/dialog';
import {trpc} from '@features/utils/trpc';
import {cn} from '@features/ui/utils';
import {useActiveScreenId} from '@features/activeScreenIdContext/useActiveScreenId';
import {useGetSelectedScreenIds} from '@features/activeScreenIdContext/useGetSelectedScreenIds';
import {getTeamDisplayName} from '@features/teams/user';
import {encodeShareParam} from '@features/flow/utils/componentScreenId';

// Context for controlling the share modal from canvas buttons
type ShareModalContextType = {
  openWithScreen: (screenId: string) => void;
};

// Context for controlling the export modal from canvas buttons
type ExportModalContextType = {
  openWithScreens: (screenIds: string[]) => void;
};

const ShareModalContext = createContext<ShareModalContextType | null>(null);
const ExportModalContext = createContext<ExportModalContextType | null>(null);

export const useShareModal = () => useContext(ShareModalContext);
const useExportModal = () => useContext(ExportModalContext);

// Provider component that wraps the page to enable share button on canvas
export function ShareModalProvider({
  flowId,
  isPrototype = false,
  children,
}: {
  flowId: string;
  isPrototype?: boolean;
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [preselectedScreenId, setPreselectedScreenId] = useState<
    string | undefined
  >(undefined);

  const openWithScreen = useCallback((screenId: string) => {
    setPreselectedScreenId(screenId);
    setIsOpen(true);
    amplitudeService.logEvent('[Share button] clicked', {
      source: 'canvas',
      screenId,
    });
  }, []);

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setPreselectedScreenId(undefined);
    }
  }, []);

  const contextValue = useMemo(() => ({openWithScreen}), [openWithScreen]);

  return (
    <ShareModalContext.Provider value={contextValue}>
      {children}
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent>
          <ShareModal
            flowId={flowId}
            isPrototype={isPrototype}
            preselectedScreenId={preselectedScreenId}
          />
        </DialogContent>
      </Dialog>
    </ShareModalContext.Provider>
  );
}

// Provider component that wraps the page to enable export button on canvas
export function ExportModalProvider({
  flowId,
  children,
}: {
  flowId: string;
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [preselectedScreenIds, setPreselectedScreenIds] = useState<
    string[] | undefined
  >(undefined);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const openWithScreens = useCallback((screenIds: string[]) => {
    // Cancel any pending close timeout when reopening
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setPreselectedScreenIds(screenIds);
    setIsOpen(true);
    amplitudeService.logEvent('[Export button] clicked', {
      source: 'canvas',
      screenCount: screenIds.length,
    });
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    // Delay resetting preselected screens until after modal close animation
    closeTimeoutRef.current = setTimeout(() => {
      setPreselectedScreenIds(undefined);
      closeTimeoutRef.current = null;
    }, 150);
  }, []);

  const contextValue = useMemo(() => ({openWithScreens}), [openWithScreens]);

  return (
    <ExportModalContext.Provider value={contextValue}>
      {children}
      <ExportModal
        flowId={flowId}
        isVisible={isOpen}
        onClose={handleClose}
        initialSelectedScreens={preselectedScreenIds}
      />
    </ExportModalContext.Provider>
  );
}

export function ShareLink({
  flowId,
  isPrototype = false,
}: {
  flowId: string;
  isPrototype?: boolean;
}) {
  const navigate = useNavigate();

  return (
    <div className='relative flex items-center gap-2' style={{zIndex: 11}}>
      {!isPrototype && (
        <>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size='sm'
                variant='secondary'
                onClick={() => {
                  amplitudeService.logEvent('[Play prototype] clicked');
                  void navigate({to: `/prototype/${flowId}`});
                }}
                className='flex h-8 items-center px-2'
              >
                <PlayIcon />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Play prototype</TooltipContent>
          </Tooltip>
          <Export />
        </>
      )}
      <Dialog>
        <DialogTrigger
          asChild
          onClick={() => amplitudeService.logEvent('[Share button] clicked')}
        >
          <Button
            variant='primary'
            size='sm'
            className='flex h-8 items-center gap-1 pr-2'
          >
            Share
            <ShareIcon />
          </Button>
        </DialogTrigger>
        <DialogContent>
          <ShareModal flowId={flowId} isPrototype={isPrototype} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ShareModal({
  flowId,
  isPrototype,
  preselectedScreenId,
}: {
  flowId: string;
  isPrototype: boolean;
  preselectedScreenId?: string;
}) {
  const [isCopied, setIsCopied] = useState(false);
  const activeScreenId = useActiveScreenId();
  const screenId = preselectedScreenId ?? activeScreenId;
  const [zoomToScreen, setZoomToScreen] = useState(!!preselectedScreenId);
  const origin = window.location.origin;
  const {data: flow} = trpc.flow.getFlow.useQuery({flowId});
  const hasTeam = flow?.teamId !== null && flow?.teamId !== undefined;
  const {data: teams} = trpc.teams.getMyTeams.useQuery();
  const currentTeam = teams?.find((t) => t.id === flow?.teamId);
  const teamDisplayName = currentTeam && getTeamDisplayName(currentTeam);

  const baseUrl = origin + (isPrototype ? '/prototype/' : '/preview/') + flowId;
  const shareParam =
    zoomToScreen && screenId ? encodeShareParam(screenId) : null;
  const shareUrl = shareParam
    ? `${baseUrl}?${new URLSearchParams({[shareParam.key]: shareParam.value})}`
    : baseUrl;

  const handleCopyLink = async () => {
    try {
      amplitudeService.logEvent('[Share button] link copied', {
        zoomToScreen,
        hasPreselectedScreen: !!preselectedScreenId,
      });
      await navigator.clipboard.writeText(shareUrl);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000); // Reset copied state after 2 seconds
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  return (
    <>
      <div className='flex flex-col gap-1'>
        <div className='font-semibold text-black'>Share</div>
        <div className='text-sm text-muted-foreground'>
          {isPrototype
            ? 'Invite others to view your prototype.'
            : 'Invite others to view your design.'}{' '}
          <a
            href='https://intercom.help/banani/en/articles/11696109-share-your-design-with-others'
            target='_blank'
            rel='noopener noreferrer'
            className='underline'
          >
            learn more
          </a>
        </div>
      </div>
      <hr className='-mx-[18px] border-black/10' />
      <div className='flex min-w-0 flex-col gap-3'>
        <Button
          variant='primary'
          size='sm'
          onClick={() => void handleCopyLink()}
          className='flex w-full items-center justify-center'
        >
          {isCopied ? 'Link copied!' : 'Copy link'}{' '}
          <LinkIcon className='h-6 w-6' />
        </Button>
        {screenId && (
          <button
            type='button'
            className='mt-1 flex cursor-pointer items-center gap-2'
            onClick={() => setZoomToScreen(!zoomToScreen)}
          >
            <div
              className={cn(
                'flex h-4 w-4 items-center justify-center rounded',
                zoomToScreen ? 'bg-primary' : 'bg-black/10',
              )}
            >
              {zoomToScreen && (
                <svg
                  width='14'
                  height='14'
                  viewBox='0 0 12 12'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M10 3L4.5 8.5L2 6'
                    stroke='white'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              )}
            </div>
            <span className='select-none text-sm font-semibold text-black'>
              {isPrototype
                ? 'Start at selected screen'
                : 'Open at selected screen'}
            </span>
          </button>
        )}
      </div>
      {teamWorkspacesEnabled && (
        <>
          <hr className='-mx-[18px] border-black/10' />
          <div className='flex flex-col gap-2'>
            <div className='text-sm font-semibold text-black'>View access</div>
            <WorkspaceSelector flowId={flowId} />
          </div>
        </>
      )}
      {hasTeam && (
        <>
          <hr className='-mx-[18px] border-black/10' />
          <div className='flex flex-col gap-2'>
            <div className='text-sm font-semibold text-black'>
              {teamDisplayName}
            </div>
            <Link
              to='/team'
              target='_blank'
              rel='noopener noreferrer'
              className='cursor-pointer rounded-lg bg-muted p-2 text-center font-semibold'
              onClick={() =>
                amplitudeService.logEvent('[Share modal] manage team clicked')
              }
            >
              Invite & manage
            </Link>
          </div>
        </>
      )}
    </>
  );
}

function Export() {
  const exportModal = useExportModal();
  const getSelectedScreenIds = useGetSelectedScreenIds();

  const handleExportClick = () => {
    amplitudeService.logEvent('[Export button] clicked');
    // Use whatever is selected (multi or single). The first id is the
    // primary active screen; the rest are extra multi-selected screens.
    const screenIds = [...getSelectedScreenIds()];
    exportModal?.openWithScreens(screenIds);
  };

  return (
    <Button
      size='sm'
      variant='secondary'
      onClick={handleExportClick}
      className='h-8'
    >
      Export
    </Button>
  );
}
